/**
 * Installing Minga as an app (decision 0113). Chromium browsers fire `beforeinstallprompt` when the app can be
 * installed; we keep the event so "Install Minga" can show the browser's own dialog later, from a click. Safari (iOS,
 * macOS) has no such event: the page explains Share › Add to Home Screen instead. `startInstallCapture` runs once at
 * boot (main.tsx), before the event can fire.
 */
import { useSyncExternalStore } from "react"

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  readonly userChoice: Promise<{ readonly outcome: "accepted" | "dismissed" }>
}

export interface Platform {
  /** iPhone or iPad (iPadOS reports a Mac with touch). */
  readonly ios: boolean
  readonly android: boolean
}

export const platformOf = (userAgent: string, maxTouchPoints = 0): Platform => ({
  ios: /iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1),
  android: /Android/.test(userAgent),
})

export const currentPlatform = (): Platform =>
  typeof navigator === "undefined"
    ? { ios: false, android: false }
    : platformOf(navigator.userAgent, navigator.maxTouchPoints ?? 0)

/** Running as the installed app (Home Screen / app window), not in a browser tab. */
export const isStandalone = (): boolean => {
  if (typeof window === "undefined") return false
  const media =
    typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches
  return media || (navigator as Navigator & { standalone?: boolean }).standalone === true
}

export interface InstallState {
  /** The browser offered installation and we hold its prompt. */
  readonly canPrompt: boolean
  /** Running installed, or installed during this visit. */
  readonly installed: boolean
}

let deferred: BeforeInstallPromptEvent | null = null
let state: InstallState = { canPrompt: false, installed: false }
const listeners = new Set<() => void>()
const emit = (next: InstallState) => {
  state = next
  for (const l of listeners) l()
}

let started = false
export const startInstallCapture = (): void => {
  if (started || typeof window === "undefined") return
  started = true
  state = { canPrompt: false, installed: isStandalone() }
  window.addEventListener("beforeinstallprompt", (event) => {
    // keep it for our own "Install Minga" button (and the phone hint) instead of the browser's mini bar
    event.preventDefault()
    deferred = event as BeforeInstallPromptEvent
    emit({ ...state, canPrompt: true })
  })
  window.addEventListener("appinstalled", () => {
    deferred = null
    emit({ canPrompt: false, installed: true })
  })
}

/** Show the browser's install dialog (from a click). Answers whether the person installed. */
export const promptInstall = async (): Promise<boolean> => {
  const event = deferred
  if (!event) return false
  deferred = null
  emit({ ...state, canPrompt: false })
  await event.prompt()
  const choice = await event.userChoice
  if (choice.outcome === "accepted") emit({ canPrompt: false, installed: true })
  return choice.outcome === "accepted"
}

const SERVER_STATE: InstallState = { canPrompt: false, installed: false }

export const useInstallState = (): InstallState =>
  useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange)
      return () => listeners.delete(onChange)
    },
    () => state,
    () => SERVER_STATE,
  )
